import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, ChevronDown, ChevronRight, Play, Award, BarChart3, Target } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const DailyAnalysis = () => {
  const { fetchPost } = useApi();
  // Set default dates - tomorrow for both start and end
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const [formData, setFormData] = useState({
    start_date: tomorrow.toISOString().split('T')[0],
    end_date: tomorrow.toISOString().split('T')[0],
    model_version: 'v3.1.0'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  const [expandedSummary, setExpandedSummary] = useState(false);
  const [customPrices, setCustomPrices] = useState({}); // Store custom prices by date
  const [editingDay, setEditingDay] = useState(null); // Track which day is being edited

  // Expand first day by default when results load
  useEffect(() => {
    if (results && results.daily_results) {
      const dates = Object.keys(results.daily_results).sort();
      if (dates.length > 0) {
        setExpandedDays({ [dates[0]]: true });
      }
    }
  }, [results]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear custom prices and editing state when model changes
    if (field === 'model_version' && value !== 'custom') {
      setCustomPrices({});
      setEditingDay(null);
    }
  };

  const runAnalysis = async () => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('start_date', formData.start_date);
      formDataToSend.append('end_date', formData.end_date);
      formDataToSend.append('model_version', formData.model_version);

      const response = await fetchPost('/daily-analysis', formDataToSend);

      const data = await response.json();
      
      if (data.status === 'success') {
        setResults(data);
      } else {
        setError(data.message || 'Analysis failed');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
      console.error('Error running analysis:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDay = (date) => {
    setExpandedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const formatSignedNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    // Format with sign explicitly shown for negative numbers
    const formatted = num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return num > 0 ? `+${formatted}` : formatted;
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  const getModelErrorColor = (error, minError, maxError) => {
    // If all errors are the same, return no background
    if (minError === maxError) return '';
    
    // Calculate intensity from 0 to 1
    const intensity = (error - minError) / (maxError - minError);
    
    // Scale from 0 to 60 for opacity (0% to 60% red)
    const opacity = Math.round(intensity * 60);
    
    // Return red with calculated opacity
    return `rgba(239, 68, 68, ${opacity / 100})`;
  };

  const renderHourlyTable = (hourlyDetails, date) => {
    if (!hourlyDetails || hourlyDetails.length === 0) return null;

    const isCustomMode = formData.model_version === 'custom';

    // Calculate min and max absolute model errors for the day (for color coding)
    const modelErrorsFixI = hourlyDetails.map(h => h.model_error_fix_i !== null && h.model_error_fix_i !== undefined ? Math.abs(h.model_error_fix_i) : null).filter(e => e !== null);
    const modelErrorsFixII = hourlyDetails.map(h => h.model_error_fix_ii !== null && h.model_error_fix_ii !== undefined ? Math.abs(h.model_error_fix_ii) : null).filter(e => e !== null);
    const allErrors = [...modelErrorsFixI, ...modelErrorsFixII];
    const minError = allErrors.length > 0 ? Math.min(...allErrors) : 0;
    const maxError = allErrors.length > 0 ? Math.max(...allErrors) : 0;

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hour</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fix I</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {isCustomMode ? 'Custom' : 'Model'}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Error vs Fix I</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fix II</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Error vs Fix II</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Closer to</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {hourlyDetails.map((hour) => {
              const displayHour = displayHours1to24 ? hour.hour + 1 : hour.hour;
              
              // Get custom price if available
              const customPrice = customPrices[date] && customPrices[date][`h${hour.hour}`];
              const displayPrice = customPrice !== undefined ? customPrice : hour.model_prediction;
              
              // Recalculate errors with custom price if available
              const modelError = customPrice !== undefined ? customPrice - hour.fix_i_price : hour.model_error_fix_i;
              const modelErrorFixII = customPrice !== undefined && hour.fix_ii_price !== null 
                ? customPrice - hour.fix_ii_price 
                : hour.model_error_fix_ii;
              
              // Calculate absolute errors for comparison
              const absErrorFixI = modelError !== null && modelError !== undefined 
                ? Math.abs(modelError) 
                : null;
              const absErrorFixII = modelErrorFixII !== null && modelErrorFixII !== undefined 
                ? Math.abs(modelErrorFixII) 
                : null;
              
              // Determine which fixing price the model is closer to
              let closerTo = 'N/A';
              let closerToColor = 'bg-gray-200 text-gray-800';
              if (absErrorFixI !== null && absErrorFixII !== null) {
                if (absErrorFixI < absErrorFixII) {
                  closerTo = 'Fix I';
                  closerToColor = 'bg-blue-200 text-blue-800';
                } else if (absErrorFixII < absErrorFixI) {
                  closerTo = 'Fix II';
                  closerToColor = 'bg-purple-200 text-purple-800';
                } else {
                  closerTo = 'Equal';
                  closerToColor = 'bg-gray-200 text-gray-800';
                }
              }
              
              const errorBgColorFixI = absErrorFixI !== null ? getModelErrorColor(absErrorFixI, minError, maxError) : '';
              const errorBgColorFixII = absErrorFixII !== null ? getModelErrorColor(absErrorFixII, minError, maxError) : '';
              
              return (
                <tr key={hour.hour}>
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {displayHour}:00
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    {formatNumber(hour.fix_i_price)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                    {formatNumber(displayPrice)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900" style={{ backgroundColor: errorBgColorFixI }}>
                    {formatSignedNumber(modelError)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    {formatNumber(hour.fix_ii_price)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900" style={{ backgroundColor: errorBgColorFixII }}>
                    {formatSignedNumber(modelErrorFixII)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${closerToColor}`}>
                      {closerTo}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const recalculateDayStats = (dayData, customPricesForDay) => {
    if (!customPricesForDay || Object.keys(customPricesForDay).length === 0) {
      return dayData;
    }

    // Recalculate errors and stats with custom prices
    let modelMAEFixI = 0;
    let modelMAEFixII = 0;
    let dailyAvgModel = 0;
    let validHoursFixI = 0;
    let validHoursFixII = 0;

    dayData.hourly_details.forEach(hour => {
      const customPrice = customPricesForDay[`h${hour.hour}`];
      if (customPrice !== undefined) {
        // MAE vs Fix I
        const modelErrorFixI = Math.abs(customPrice - hour.fix_i_price);
        modelMAEFixI += modelErrorFixI;
        dailyAvgModel += customPrice;
        validHoursFixI++;
        
        // MAE vs Fix II (if available)
        if (hour.fix_ii_price !== null && hour.fix_ii_price !== undefined) {
          const modelErrorFixII = Math.abs(customPrice - hour.fix_ii_price);
          modelMAEFixII += modelErrorFixII;
          validHoursFixII++;
        }
      }
    });

    if (validHoursFixI > 0) {
      modelMAEFixI = modelMAEFixI / validHoursFixI;
      dailyAvgModel = dailyAvgModel / validHoursFixI;
      
      const updatedData = {
        ...dayData,
        model_mae: modelMAEFixI,
        daily_avg_model: dailyAvgModel,
        daily_model_mae_from_avg: Math.abs(dailyAvgModel - dayData.daily_avg_actual)
      };
      
      // Add Fix II MAE if we have data
      if (validHoursFixII > 0) {
        updatedData.model_mae_fix_ii = modelMAEFixII / validHoursFixII;
      }
      
      return updatedData;
    }
    
    return dayData;
  };

  const renderDayResult = (date, dayData) => {
    const isExpanded = expandedDays[date];
    const customPricesForDay = customPrices[date];
    const displayData = customPricesForDay && Object.keys(customPricesForDay).length > 0
      ? recalculateDayStats(dayData, customPricesForDay)
      : dayData;

    return (
      <div key={date} className="border rounded-lg mb-4 overflow-hidden">
        <div 
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors bg-white"
          onClick={() => toggleDay(date)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="font-semibold text-lg">{date}</span>
              {customPricesForDay && Object.keys(customPricesForDay).length > 0 && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                  Custom Prices Applied
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-6">
              {/* Daily Averages */}
              <div className="text-sm">
                <span className="text-gray-500">Daily Avg:</span>
                <span className="ml-2 font-medium">Fix I: {formatNumber(displayData.daily_avg_actual)}</span>
                <span className="ml-3 font-medium">Fix II: {formatNumber(displayData.daily_avg_fix_ii)}</span>
                <span className="ml-3 font-medium">Model: {formatNumber(displayData.daily_avg_model)}</span>
              </div>
            </div>
          </div>
          
          {/* Day Statistics Summary */}
          <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Model MAE vs Fix I:</span>
              <span className="ml-2 font-medium">{formatNumber(displayData.model_mae)}</span>
            </div>
            <div>
              <span className="text-gray-500">Model MAE vs Fix II:</span>
              <span className="ml-2 font-medium">{formatNumber(displayData.model_mae_fix_ii)}</span>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 bg-gray-50 border-t">
            {/* Custom price input section for custom mode */}
            {formData.model_version === 'custom' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-700">Custom Prices Input</h4>
                  {editingDay !== date ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingDay(date);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      Edit Custom Prices
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Parse the textarea content
                          const textarea = document.getElementById(`custom-prices-${date}`);
                          if (textarea) {
                            const prices = textarea.value.split('\n').filter(v => v.trim() !== '');
                            if (prices.length === 24) {
                              const newCustomPrices = {};
                              prices.forEach((price, index) => {
                                const value = parseFloat(price.trim());
                                if (!isNaN(value)) {
                                  newCustomPrices[`h${index}`] = value;
                                }
                              });
                              setCustomPrices(prev => ({
                                ...prev,
                                [date]: newCustomPrices
                              }));
                              setEditingDay(null);
                            } else {
                              alert(`Please provide exactly 24 prices (one per line). You provided ${prices.length}.`);
                            }
                          }
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        Apply
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingDay(null);
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                
                {editingDay === date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      Paste 24 hourly prices (one per line, for hours 0-23):
                    </p>
                    <textarea
                      id={`custom-prices-${date}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      placeholder="100.5&#10;102.3&#10;98.7&#10;...&#10;(24 prices total)"
                      defaultValue={
                        customPricesForDay && Object.keys(customPricesForDay).length === 24
                          ? Array.from({length: 24}, (_, i) => customPricesForDay[`h${i}`] || '').join('\n')
                          : ''
                      }
                    />
                  </div>
                )}
                
                {customPricesForDay && Object.keys(customPricesForDay).length === 24 && editingDay !== date && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      ✓ Custom prices applied. Click "Edit Custom Prices" to modify.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div className="mb-3">
              <h4 className="font-semibold text-gray-700">Hourly Comparison</h4>
            </div>
            {renderHourlyTable(dayData.hourly_details, date)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Daily Analysis</h1>
          <p className="text-gray-600">Compare model predictions vs Meteologica predictions against actual prices</p>
        </div>

        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>Start Date</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} />
                <span>End Date</span>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp size={16} />
                <span>Model Version</span>
              </label>
              <select
                value={formData.model_version}
                onChange={(e) => handleInputChange('model_version', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v3.1.0">v3.1.0 (Probabilistic)</option>
                {/* <option value="v0.2.0">v0.2.0</option>
                <option value="v0.2.1">v0.2.1</option>
                <option value="v0.2.2">v0.2.2</option>
                <option value="v0.2.3">v0.2.3</option>
                <option value="v0.2.3.1">v0.2.3.1</option>
                <option value="v0.2.3.2">v0.2.3.2</option>
                <option value="v0.2.4">v0.2.4</option>
                <option value="v0.2.5">v0.2.5</option>
                <option value="v0.2.5-stacked">v0.2.5-stacked</option>
                <option value="v0.2.6-stacked">v0.2.6-stacked</option>
                <option value="v0.2.5-stacked-weighted-l0-s1">v0.2.5-stacked-weighted-l0-s1</option>
                <option value="v0.2.5-stacked-weighted-l0.6-s0.4">v0.2.5-stacked-weighted-l0.6-s0.4</option>
                <option value="v0.2.6-stacked-warm-up">v0.2.6-stacked-warm-up</option>
                <option value="v0.2.6-stacked-warm-up-after-hp">v0.2.6-stacked-warm-up-after-hp</option>
                <option value="v0.2.6-stacked-warm-up-after-hp-ensemble10">v0.2.6-stacked-warm-up-after-hp-ensemble10</option>
                <option value="v0.2.4.1">v0.2.4.1</option>
                <option value="v0.2.7">v0.2.7</option>
                <option value="v0.2.7.1">v0.2.7.1</option>
                <option value="v0.2.7.2">v0.2.7.2</option>
                <option value="v0.3.0">v0.3.0</option>
                <option value="v0.3.1">v0.3.1</option>
                <option value="v0.4.0">v0.4.0</option>
                <option value="v0.4.1">v0.4.1</option> */}
                <option value="v1.0.0">v1.0.0</option>
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v1.0.0-fix2-cp14">v1.0.0-fix2-cp14</option>
                <option value="v1.0.0-fix2-exaa">v1.0.0-fix2-exaa</option>
                <option value="v1.0.1">v1.0.1</option>
                <option value="v1.0.2">v1.0.2</option>
                <option value="v1.0.3">v1.0.3</option>
                <option value="v1.0.3.1">v1.0.3.1</option>
                <option value="v2.0.0">v2.0.0</option>
                <option value="v2.0.0-cp14">v2.0.0-cp14</option>
                <option value="v2.0.0-exaa">v2.0.0-exaa</option>
                <option value="v2.0.0-hourly">v2.0.0-hourly</option>
                <option value="v2.0.0-l1">v2.0.0-l1</option>
                <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                <option value="v2.0.0-narx">v2.0.0-narx</option>
                <option value="v2.0.0-narx-single">v2.0.0-narx-single</option>
                <option value="v2.0.0-narx-fix2">v2.0.0-narx-fix2</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={runAnalysis}
                disabled={isLoading}
                className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  isLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-[1.02]'
                }`}
              >
                <Play size={20} />
                <span>{isLoading ? 'Running Analysis...' : 'Run Analysis'}</span>
              </button>
            </div>
          </div>
          
          {/* Hour display format toggle */}
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-700">Display hours as 1-24</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={displayHours1to24}
                  onChange={(e) => setDisplayHours1to24(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            
            {/* Help text for custom mode */}
            {formData.model_version === 'custom' && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Custom Mode:</strong> Run analysis to see actual prices, then click "Edit Custom Prices" 
                  on each day to input your own predictions. Errors will be recalculated automatically.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Results Summary */}
        {results && results.summary && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
            <div 
              className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedSummary(!expandedSummary)}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                  {expandedSummary ? <ChevronDown className="mr-2" size={24} /> : <ChevronRight className="mr-2" size={24} />}
                  Summary
                </h2>
                
                {/* Quick stats in header when collapsed */}
                {!expandedSummary && (
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-600">
                      Winner: <span className="font-semibold">{results.summary.overall_winner === 'model' ? 'Model' : 'Meteologica'}</span>
                    </span>
                    <span className="text-gray-600">
                      Period: {results.date_range.start} to {results.date_range.end}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {expandedSummary && (
              <div className="px-6 pb-6 border-t">
                {/* Overall Winner */}
                <div className="mt-6 mb-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Award className="w-8 h-8 text-yellow-500" />
                      <div>
                        <p className="text-sm text-gray-600">Overall Winner (by MAE)</p>
                        <p className="text-xl font-bold">
                          {results.summary.overall_winner === 'model' ? 'Model' : 'Meteologica'}
                        </p>
                        <p className="text-sm text-gray-600">
                          MAE advantage: {formatNumber(results.summary.winner_mae_advantage)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Analysis Period</p>
                      <p className="font-medium">{results.date_range.start} to {results.date_range.end}</p>
                      <p className="text-sm text-gray-600">{results.summary.total_days} days, {results.summary.hours_with_data} hours</p>
                    </div>
                  </div>
                </div>
                
                {/* Statistics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Hourly Statistics */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Hourly Comparison
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Win Count</span>
                    <div>
                      <span className="text-green-600 font-medium">Model: {results.summary.model_wins}</span>
                      <span className="mx-2">|</span>
                      <span className="text-blue-600 font-medium">Meteo: {results.summary.meteologica_wins}</span>
                      <span className="mx-2">|</span>
                      <span className="text-gray-600 font-medium">Ties: {results.summary.ties}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Win Rate</span>
                    <div>
                      <span className="text-green-600 font-medium">Model: {formatPercentage(results.summary.model_win_rate)}</span>
                      <span className="mx-2">|</span>
                      <span className="text-blue-600 font-medium">Meteo: {formatPercentage(results.summary.meteologica_win_rate)}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average MAE</span>
                    <div>
                      <span className={`font-medium ${results.summary.model_mae_avg < results.summary.meteologica_mae_avg ? 'text-green-600' : 'text-gray-600'}`}>
                        Model: {formatNumber(results.summary.model_mae_avg)}
                      </span>
                      <span className="mx-2">|</span>
                      <span className={`font-medium ${results.summary.meteologica_mae_avg < results.summary.model_mae_avg ? 'text-blue-600' : 'text-gray-600'}`}>
                        Meteo: {formatNumber(results.summary.meteologica_mae_avg)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average RMSE</span>
                    <div>
                      <span className="font-medium">Model: {formatNumber(results.summary.model_rmse_avg)}</span>
                      <span className="mx-2">|</span>
                      <span className="font-medium">Meteo: {formatNumber(results.summary.meteologica_rmse_avg)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Daily Average Statistics */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2" />
                  Daily Average Comparison
                </h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Win Count</span>
                    <div>
                      <span className="text-green-600 font-medium">Model: {results.summary.daily_avg_model_wins}</span>
                      <span className="mx-2">|</span>
                      <span className="text-blue-600 font-medium">Meteo: {results.summary.daily_avg_meteologica_wins}</span>
                      <span className="mx-2">|</span>
                      <span className="text-gray-600 font-medium">Ties: {results.summary.daily_avg_ties}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Win Rate</span>
                    <div>
                      <span className="text-green-600 font-medium">Model: {formatPercentage(results.summary.daily_avg_model_win_rate)}</span>
                      <span className="mx-2">|</span>
                      <span className="text-blue-600 font-medium">Meteo: {formatPercentage(results.summary.daily_avg_meteologica_win_rate)}</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average MAE</span>
                    <div>
                      <span className={`font-medium ${results.summary.daily_avg_model_mae_avg < results.summary.daily_avg_meteologica_mae_avg ? 'text-green-600' : 'text-gray-600'}`}>
                        Model: {formatNumber(results.summary.daily_avg_model_mae_avg)}
                      </span>
                      <span className="mx-2">|</span>
                      <span className={`font-medium ${results.summary.daily_avg_meteologica_mae_avg < results.summary.daily_avg_model_mae_avg ? 'text-blue-600' : 'text-gray-600'}`}>
                        Meteo: {formatNumber(results.summary.daily_avg_meteologica_mae_avg)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
              </div>
            )}
          </div>
        )}

        {/* Daily Results */}
        {results && results.daily_results && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Daily Results</h2>
            <div className="space-y-2">
              {Object.entries(results.daily_results)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, data]) => renderDayResult(date, data))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyAnalysis;
